import assertNever from "assert-never"

import { BaseEditorState } from "./common.types"

const NFLRequestNames = ["info", "games", "players", "team", "stats"] as const
export type NFLRequest = typeof NFLRequestNames[number] | never
export const NFLRequest = {
  all: NFLRequestNames
}

type NflEditorStateForChannel<C extends NFLRequest> = C extends "games"
  ? NflGamesEditorState
  : C extends "info"
  ? NflInfoEditorState
  : C extends "stats"
  ? NflStatsEditorState
  : C extends "team"
  ? NflTeamEditorState
  : C extends "players"
  ? NflPlayersEditorState
  : never

export type NflEditorState =
  | NflStatsEditorState
  | NflInfoEditorState
  | NflGamesEditorState
  | NflPlayersEditorState
  | NflTeamEditorState

export const NflEditorState = {
  /** Converts the editor state to a path we can send a REST path to  */
  getPath: (state: NflEditorState): string => {
    switch (state.channel) {
      case "games": {
        return `/nfl/v0/games/${state.week}/${state.seasonPhase}/${state.year}/${state.teamId}`
      }
      case "info": {
        return `/nfl/v0/info`
      }
      case "players": {
        return `/nfl/v0/players/${state.lastName}/${state.firstName}`
      }
      case "stats": {
        return `/nfl/v0/stats/${state.statType}/${state.year}/${state.week}/${state.seasonPhase}/${state.lastName}/${state.firstName}`
      }
      case "team": {
        return `/nfl/v0/team/${state.teamId}/${state.retrieve}/${state.year}`
      }
      default:
        return assertNever(state)
    }
  }
}

export const DefaultNflRequestsForChannel: {
  [channel in NFLRequest]: NflEditorStateForChannel<channel>
} = {
  games: {
    api: "NFL",
    teamId: "ARI",
    channel: "games",
    week: 1,
    seasonPhase: "regular",
    year: 2018
  },
  info: {
    api: "NFL",
    channel: "info"
  },
  players: {
    api: "NFL",
    channel: "players",
    lastName: "Brady",
    firstName: "Tom"
  },
  stats: {
    api: "NFL",
    channel: "stats",
    lastName: "Brady",
    firstName: "Tom",
    year: 2018,
    week: 3,
    seasonPhase: "postseason",
    statType: "passing"
  },
  team: {
    api: "NFL",
    channel: "team",
    teamId: "CHI",
    retrieve: "schedule",
    year: 2018
  }
}

interface NflPlayersEditorState extends BaseNflEditorState<"players"> {
  lastName: string
  firstName: string
}

const allNflTeamIds = [
  "ARI",
  "ATL",
  "BAL",
  "BUF",
  "CAR",
  "CHI",
  "CIN",
  "CLE",
  "DAL",
  "DEN",
  "DET",
  "GB ",
  "HOU",
  "IND",
  "JAC",
  "KC ",
  "LA",
  "MIA",
  "MIN",
  "NE",
  "NO",
  "NYG",
  "NYJ",
  "OAK",
  "PHI",
  "PIT",
  "SD",
  "SEA",
  "SF",
  "TB",
  "TEN",
  "WAS"
] as const

export type NflTeam = typeof allNflTeamIds[number] | never
export const NflTeam = {
  all: allNflTeamIds
}

// TODO is this correct?
const allNflWeeks = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18
] as const

export type NflWeek = typeof allNflWeeks[number] | never
export const NflWeek = {
  all: allNflWeeks
}

const allSeasonPhases = ["regular", "preseason", "postseason"] as const

export type NflSeasonPhase = typeof allSeasonPhases[number] | never
export const NflSeasonPhase = {
  all: allSeasonPhases
}

const allYears = [
  2010,
  2011,
  2012,
  2013,
  2014,
  2015,
  2016,
  2017,
  2018,
  2019
] as const

export type NflYear = typeof allYears[number] | never
export const NflYear = {
  all: allYears
}

interface NflGamesEditorState extends BaseNflEditorState<"games"> {
  teamId: NflTeam
  week: NflWeek
  seasonPhase: NflSeasonPhase
  year: NflYear
}

interface NflTeamEditorState extends BaseNflEditorState<"team"> {
  teamId: NflTeam
  retrieve: "schedule" | "roster"
  year: NflYear
}

const allStatTypes = ["passing", "rushing", "receiving", "defense"] as const

export type NflStatType = typeof allStatTypes[number] | never
export const NflStatType = {
  all: allStatTypes
}

interface NflStatsEditorState extends BaseNflEditorState<"stats"> {
  lastName: string
  firstName: string
  year: NflYear
  week: NflWeek
  seasonPhase: NflSeasonPhase
  statType: NflStatType // TODO
}

interface NflInfoEditorState extends BaseNflEditorState<"info"> {}

interface BaseNflEditorState<C extends NFLRequest>
  extends BaseEditorState<"NFL"> {
  channel: C
}
